// Coordinates for people: UTM easting/northing (the atlas CRS) -> latitude/longitude, and formatting.
// NAD83 and WGS84 agree to about a metre here, so the GRS80 ellipsoid serves both.

let ZONE = 17, SOUTH = false, CONV = 0;

export function setCrs(crs, convergence = 0) {
  const m = /EPSG:(\d+)/.exec(crs || '');
  if (m) {
    const c = +m[1];
    if (c >= 26901 && c <= 26923) ZONE = c - 26900;            // NAD83 / UTM zone N
    else if (c >= 32601 && c <= 32660) ZONE = c - 32600;       // WGS 84 / UTM zone N
    else if (c >= 32701 && c <= 32760) { ZONE = c - 32700; SOUTH = true; }
  }
  CONV = convergence || 0;
}
export const utmZone = () => `${ZONE}${SOUTH ? 'S' : 'N'}`;

export function toLatLon(E, N) {
  const a = 6378137, f = 1 / 298.257222101, e2 = f * (2 - f), k0 = 0.9996, ep2 = e2 / (1 - e2);
  const x = E - 500000, y = SOUTH ? N - 10000000 : N;
  const M = y / k0, mu = M / (a * (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 ** 3 / 256));
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const p1 = mu + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu) + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu)
    + (151 * e1 ** 3 / 96) * Math.sin(6 * mu) + (1097 * e1 ** 4 / 512) * Math.sin(8 * mu);
  const s = Math.sin(p1), c = Math.cos(p1), t = Math.tan(p1);
  const C1 = ep2 * c * c, T1 = t * t, N1 = a / Math.sqrt(1 - e2 * s * s), R1 = a * (1 - e2) / (1 - e2 * s * s) ** 1.5, D = x / (N1 * k0);
  const lat = p1 - (N1 * t / R1) * (D * D / 2 - (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D ** 4 / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) * D ** 6 / 720);
  const lon0 = ((ZONE - 1) * 6 - 180 + 3) * Math.PI / 180;
  const lon = lon0 + (D - (1 + 2 * T1 + C1) * D ** 3 / 6 + (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D ** 5 / 120) / c;
  return [lat * 180 / Math.PI, lon * 180 / Math.PI];
}

export const fmtLL = (lat, lon, dp = 6) => `${lat.toFixed(dp)}, ${lon.toFixed(dp)}`;
function dms(v, pos, neg) {
  const h = v < 0 ? neg : pos; v = Math.abs(v);
  const d = Math.floor(v), m = Math.floor((v - d) * 60), s = ((v - d) * 60 - m) * 60;
  return `${d}°${String(m).padStart(2, '0')}′${s.toFixed(1).padStart(4, '0')}″${h}`;
}
export const fmtDMS = (lat, lon) => `${dms(lat, 'N', 'S')} ${dms(lon, 'E', 'W')}`;
export const fmtUTM = (E, N) => `${utmZone()} ${Math.round(E)} E ${Math.round(N)} N`;
const FT = 0.3048;
export const fmtLen = (m) => (m == null ? '—' : m >= 1000 ? `${(m / 1000).toFixed(2)} km (${(m / 1609.344).toFixed(2)} mi)`
  : `${m.toFixed(m < 10 ? 1 : 0)} m (${Math.round(m / FT).toLocaleString()} ft)`);
export const fmtElev = (m) => (m == null ? '—' : `${m.toFixed(1)} m (${Math.round(m / FT).toLocaleString()} ft)`);
// true bearing of a grid vector (grid north differs from true north by the convergence)
export function bearing(dE, dN) {
  return ((Math.atan2(dE, dN) * 180 / Math.PI + CONV) % 360 + 360) % 360;
}
const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
export const compass = (deg) => POINTS[Math.round(deg / 22.5) % 16];
export const mapsLink = (lat, lon) => `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lon.toFixed(6)}`;

export function copy(text) {
  try { navigator.clipboard.writeText(text); return true; } catch { return false; }
}

export function download(name, text, type = 'text/plain') {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type })); a.download = name;
  document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}
